import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Checkbox,
  FormGroup,
  Card,
  CardContent,
  Chip,
  Alert,
  Container,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Link as RouterLink, useLocation, useHistory, useParams } from "react-router-dom";
import ResponsiveDrawer from "views/components/ResponsiveDrawer";
import tsumuraKampoData from "data/tsumura-kampo.json";
import allKampoData from "data/all-kampo.json";
import hakkoData from "data/hakko-benzou.json";
import kiketsushinekiData from "data/kiketsushineki-benzou.json";
import zoufuData from "data/zoufu-benzou.json";
import rokkeiData from "data/rokkei-benzou.json";
import integratedData from "data/integrated-benzou.json";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  buttonGroup: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(3),
  },
  resultCard: {
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  },
  kampoChip: {
    margin: theme.spacing(0.5),
  },
  symptomGroup: {
    marginTop: theme.spacing(2),
  },
}));

function ShoDiagnosis() {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const { type } = useParams();
  const [activeStep, setActiveStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selectedSymptoms, setSelectedSymptoms] = useState([]);
  const [diagnosisResult, setDiagnosisResult] = useState(null);
  const [resultTabValue, setResultTabValue] = useState(0);
  const [diagnosisData, setDiagnosisData] = useState(null);

  const steps = ["体質チェック", "症状選択", "結果表示"];

  // 診断タイプに応じたデータを読み込む
  useEffect(() => {
    const dataMap = {
      integrated: integratedData,
      hakko: hakkoData,
      kiketsushineki: kiketsushinekiData,
      zoufu: zoufuData,
      rokkei: rokkeiData,
    };

    const data = dataMap[type];
    if (data) {
      setDiagnosisData(data);
      // 診断タイプが変更されたら状態をリセット（URLパラメータがない場合のみ）
      const params = new URLSearchParams(location.search);
      if (!params.get('answers') && !params.get('symptoms')) {
        setActiveStep(0);
        setAnswers({});
        setSelectedSymptoms([]);
        setDiagnosisResult(null);
        setResultTabValue(0);
      }
    } else {
      // デフォルトまたはエラー処理
      console.error(`Unknown diagnosis type: ${type}`);
    }
  }, [type, location.search]);

  // URLパラメータから状態を復元
  useEffect(() => {
    if (!diagnosisData) return; // データが読み込まれるまで待つ
    
    const params = new URLSearchParams(location.search);
    const answersParam = params.get('answers');
    const symptomsParam = params.get('symptoms');
    
    if (answersParam && symptomsParam) {
      try {
        const decodedAnswers = JSON.parse(decodeURIComponent(answersParam));
        const decodedSymptoms = JSON.parse(decodeURIComponent(symptomsParam));
        
        setAnswers(decodedAnswers);
        setSelectedSymptoms(decodedSymptoms);
        
        // URLパラメータがある場合は自動で判定を実行して結果ページへ
        setActiveStep(2);
        
        // 判定実行
        const result = calculateDiagnosis(decodedAnswers, decodedSymptoms);
        setDiagnosisResult(result);
      } catch (e) {
        console.error('Failed to parse parameters from URL', e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, diagnosisData]);

  // データが読み込まれるまでローディング表示
  if (!diagnosisData) {
    return (
      <ResponsiveDrawer>
        <div style={{ maxWidth: "calc(100vw - 48px)" }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        </div>
      </ResponsiveDrawer>
    );
  }

  // URLを更新する関数
  const updateURL = (answersData, symptomsData) => {
    const params = new URLSearchParams();
    params.set('answers', encodeURIComponent(JSON.stringify(answersData)));
    params.set('symptoms', encodeURIComponent(JSON.stringify(symptomsData)));
    history.push(`${location.pathname}?${params.toString()}`);
  };

  // タブ変更ハンドラ
  const handleResultTabChange = (event, newValue) => {
    setResultTabValue(newValue);
  };

  // 漢方薬の情報を取得する関数
  const getKampoInfo = (kampoName) => {
    // ツムラの漢方薬データから検索
    const tsumuraKampo = tsumuraKampoData.find(
      (item) => item.name === kampoName
    );

    // all-kampo.jsonから検索（組み合わせ再現漢方検索用）
    const allKampo = allKampoData.find(
      (item) => item.name === kampoName
    );

    return {
      name: kampoName,
      tsumuraNo: tsumuraKampo ? tsumuraKampo.product_no : null,
      existsInAllKampo: !!allKampo,
    };
  };

  const handleNext = () => {
    if (activeStep === steps.length - 2) {
      // 証の判定を実行
      performDiagnosis();
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setAnswers({});
    setSelectedSymptoms([]);
    setDiagnosisResult(null);
    setResultTabValue(0);
    // URLパラメータをクリア
    history.push(location.pathname);
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers({
      ...answers,
      [questionId]: value,
    });
  };

  const handleSymptomChange = (symptomId) => {
    setSelectedSymptoms((prev) => {
      if (prev.includes(symptomId)) {
        return prev.filter((id) => id !== symptomId);
      } else {
        return [...prev, symptomId];
      }
    });
  };

  // 診断ロジックを関数化
  const calculateDiagnosis = (answersData, symptomsData) => {
    // スコアを計算
    let kyojitsuScore = 0;
    let kannetsuScore = 0;
    const kbsScores = { qi: 0, blood: 0, water: 0, heat: 0 };

    // 質問の回答からスコアを計算
    diagnosisData.questions.forEach((question) => {
      const answer = answersData[question.id];
      if (answer) {
        const option = question.options.find((opt) => opt.value === answer);
        if (option) {
          if (typeof option.score === "number") {
            if (question.category === "虚実") {
              kyojitsuScore += option.score;
            } else if (question.category === "寒熱") {
              kannetsuScore += option.score;
            }
          } else if (typeof option.score === "object") {
            Object.keys(option.score).forEach((key) => {
              if (key in kbsScores) {
                kbsScores[key] += option.score[key];
              }
            });
          }
        }
      }
    });

    // 虚実・寒熱の判定
    let kyojitsu = "medium";
    if (kyojitsuScore >= 2) kyojitsu = "jitsu";
    else if (kyojitsuScore <= -2) kyojitsu = "kyo";

    let kannetsu = "normal";
    if (kannetsuScore >= 2) kannetsu = "heat";
    else if (kannetsuScore <= -2) kannetsu = "cold";

    // 証パターンをマッチング
    const matchedPatterns = [];

    // 虚実寒熱パターンのマッチング
    diagnosisData.shoPatterns.forEach((pattern) => {
      if (pattern.criteria) {
        const kyojitsuMatch =
          !pattern.criteria.kyojitsu ||
          pattern.criteria.kyojitsu === kyojitsu ||
          kyojitsu === "medium";
        const kannetsuMatch =
          !pattern.criteria.kannetsu ||
          pattern.criteria.kannetsu === kannetsu ||
          kannetsu === "normal";

        if (kyojitsuMatch && kannetsuMatch && pattern.criteria.kyojitsu) {
          matchedPatterns.push({
            pattern,
            score: 10,
            reason: "体質パターンマッチ",
          });
        }
      }
    });

    // 症状ベースのパターンマッチング
    diagnosisData.shoPatterns.forEach((pattern) => {
      if (pattern.symptoms) {
        const matchCount = pattern.symptoms.filter((symptomId) =>
          symptomsData.includes(symptomId)
        ).length;

        if (matchCount > 0) {
          const existingPattern = matchedPatterns.find(
            (mp) => mp.pattern.id === pattern.id
          );
          if (existingPattern) {
            existingPattern.score += matchCount * 5;
          } else {
            matchedPatterns.push({
              pattern,
              score: matchCount * 5,
              reason: `${matchCount}個の症状が一致`,
            });
          }
        }
      }
    });

    // スコアでソート
    matchedPatterns.sort((a, b) => b.score - a.score);

    return {
      kyojitsu,
      kannetsu,
      kbsScores,
      patterns: matchedPatterns.slice(0, 3), // 上位3つ
      selectedSymptoms: symptomsData,
    };
  };

  const performDiagnosis = () => {
    const result = calculateDiagnosis(answers, selectedSymptoms);
    setDiagnosisResult(result);
    
    // URLを更新
    updateURL(answers, selectedSymptoms);
  };

  const getKyojitsuLabel = (value) => {
    switch (value) {
      case "jitsu":
        return "実証";
      case "kyo":
        return "虚証";
      default:
        return "中間証";
    }
  };

  const getKannetsuLabel = (value) => {
    switch (value) {
      case "heat":
        return "熱証";
      case "cold":
        return "寒証";
      default:
        return "平証";
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              体質に関する質問にお答えください
            </Typography>
            {diagnosisData.questions.map((question) => (
              <Paper key={question.id} className={classes.paper}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">{question.question}</FormLabel>
                  <RadioGroup
                    value={answers[question.id] || ""}
                    onChange={(e) =>
                      handleAnswerChange(question.id, e.target.value)
                    }
                  >
                    {question.options.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio />}
                        label={option.label}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Paper>
            ))}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              当てはまる症状を選択してください（複数選択可）
            </Typography>
            <Paper className={classes.paper}>
              <FormGroup>
                {diagnosisData.symptoms.map((symptom) => (
                  <FormControlLabel
                    key={symptom.id}
                    control={
                      <Checkbox
                        checked={selectedSymptoms.includes(symptom.id)}
                        onChange={() => handleSymptomChange(symptom.id)}
                      />
                    }
                    label={symptom.label}
                  />
                ))}
              </FormGroup>
            </Paper>
          </Box>
        );

      case 2:
        if (!diagnosisResult) return null;

        return (
          <Box>
            <Typography variant="h5" sx={{ mb: 2 }}>
              判定結果
            </Typography>

            <Paper sx={{ mb: 3 }}>
              <Tabs value={resultTabValue} onChange={handleResultTabChange} aria-label="結果タブ">
                <Tab label="判定結果" />
                <Tab label="問診票結果" />
              </Tabs>
            </Paper>

            {/* タブ1: 判定結果 */}
            {resultTabValue === 0 && (
              <Box>
                <Paper className={classes.paper}>
                  <Typography variant="h6" gutterBottom>
                    基本体質
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                    <Chip
                      label={`虚実: ${getKyojitsuLabel(diagnosisResult.kyojitsu)}`}
                      color="primary"
                      size="large"
                    />
                    <Chip
                      label={`寒熱: ${getKannetsuLabel(diagnosisResult.kannetsu)}`}
                      color="secondary"
                      size="large"
                    />
                  </Box>

                  {diagnosisResult.selectedSymptoms.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        選択された症状:
                      </Typography>
                      <Box>
                        {diagnosisResult.selectedSymptoms.map((symptomId) => {
                          const symptom = diagnosisData.symptoms.find(
                            (s) => s.id === symptomId
                          );
                          return (
                            <Chip
                              key={symptomId}
                              label={symptom?.label}
                              className={classes.kampoChip}
                              size="small"
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Paper>

                {diagnosisResult.patterns.length > 0 ? (
                  <>
                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                      あなたの証の傾向と参考漢方薬
                    </Typography>
                    {diagnosisResult.patterns.map((item, index) => (
                      <Card key={item.pattern.id} className={classes.resultCard} sx={{ mb: 2 }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {index + 1}. {item.pattern.name}
                          </Typography>
                          <Typography variant="body2" paragraph>
                            {item.pattern.description}
                          </Typography>
                          <Typography variant="subtitle2" gutterBottom>
                            参考となる漢方薬:
                          </Typography>
                          <Box>
                            {item.pattern.recommendedKampo.map((kampoName) => {
                              const kampoInfo = getKampoInfo(kampoName);
                              
                              // ツムラの番号がある場合
                              if (kampoInfo.tsumuraNo) {
                                return (
                                  <Chip
                                    key={kampoName}
                                    label={`${kampoName} (ツムラ ${kampoInfo.tsumuraNo})`}
                                    className={classes.kampoChip}
                                    color="default"
                                  />
                                );
                              }
                              
                              // ツムラにないが、all-kampo.jsonに存在する場合
                              if (kampoInfo.existsInAllKampo) {
                                return (
                                  <Chip
                                    key={kampoName}
                                    label={kampoName}
                                    className={classes.kampoChip}
                                    color="default"
                                    component={RouterLink}
                                    to={`/reproduce-search?q=${encodeURIComponent(kampoName)}`}
                                    clickable
                                    sx={{
                                      textDecoration: 'none',
                                      '&:hover': {
                                        textDecoration: 'underline',
                                      }
                                    }}
                                  />
                                );
                              }
                              
                              // どちらにも存在しない場合
                              return (
                                <Chip
                                  key={kampoName}
                                  label={kampoName}
                                  className={classes.kampoChip}
                                  color="default"
                                />
                              );
                            })}
                          </Box>
                        </CardContent>
                      </Card>
                    ))}

                    <Alert severity="warning" sx={{ mt: 3 }}>
                      <strong>重要：</strong>この判定結果は参考情報であり、医療行為ではありません。実際の漢方薬の選択・服用については、必ず医師または薬剤師にご相談ください。
                    </Alert>
                  </>
                ) : (
                  <Alert severity="warning" sx={{ mt: 3 }}>
                    該当する証パターンが見つかりませんでした。より詳しい判定は医師または薬剤師などの専門家にご相談ください。
                  </Alert>
                )}
              </Box>
            )}

            {/* タブ2: 問診票結果 */}
            {resultTabValue === 1 && (
              <Box>
                <Paper className={classes.paper}>
                  <Typography variant="h6" gutterBottom>
                    体質チェック回答
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>質問</strong></TableCell>
                          <TableCell><strong>回答</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {diagnosisData.questions.map((question) => {
                          const answer = answers[question.id];
                          const option = question.options.find((opt) => opt.value === answer);
                          return (
                            <TableRow key={question.id}>
                              <TableCell>{question.question}</TableCell>
                              <TableCell>{option ? option.label : '未回答'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>

                <Paper className={classes.paper} sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    選択された症状
                  </Typography>
                  {selectedSymptoms.length > 0 ? (
                    <Box>
                      {selectedSymptoms.map((symptomId) => {
                        const symptom = diagnosisData.symptoms.find(
                          (s) => s.id === symptomId
                        );
                        return (
                          <Chip
                            key={symptomId}
                            label={symptom?.label}
                            className={classes.kampoChip}
                            color="primary"
                          />
                        );
                      })}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      症状は選択されていません
                    </Typography>
                  )}
                </Paper>
              </Box>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  const isStepValid = (step) => {
    switch (step) {
      case 0:
        return Object.keys(answers).length >= diagnosisData.questions.length;
      case 1:
        return true; // 症状選択は任意
      default:
        return true;
    }
  };

  return (
    <ResponsiveDrawer>
      <div style={{ maxWidth: "calc(100vw - 48px)" }}>
        <Box sx={{ mb: 2 }}>
          <Typography paragraph>
            {diagnosisData.name}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {diagnosisData.description}
          </Typography>
        </Box>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box>
          {renderStepContent(activeStep)}

          <div className={classes.buttonGroup}>
            <Button disabled={activeStep === 0} onClick={handleBack}>
              戻る
            </Button>
            <Box>
              {activeStep === steps.length - 1 ? (
                <Button variant="contained" color="primary" onClick={handleReset}>
                  最初からやり直す
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                  disabled={!isStepValid(activeStep)}
                >
                  {activeStep === steps.length - 2 ? "判定する" : "次へ"}
                </Button>
              )}
            </Box>
          </div>
        </Box>
      </div>
    </ResponsiveDrawer>
  );
}

export default ShoDiagnosis;
